import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import "./auth.css";
import { normalizeLang } from "../i18n";
import { resolvePostAuthTarget } from "./postAuthTarget";
import LegalFooterLinks from "../components/LegalFooterLinks";

function useNextPath() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  return resolvePostAuthTarget(params.get("next"), lang);
}

export default function AuthRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = useNextPath();
  const { register } = useAuth();
  const { t, i18n } = useTranslation();
  /** רק אם יש ?lang= ב־URL — אחרת משמרים את השפה מ־LanguageBar / i18n */
  const urlLangExplicit = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("lang");
    if (raw == null || String(raw).trim() === "") return null;
    return normalizeLang(raw);
  }, [location.search]);
  const langForLinks = urlLangExplicit ?? normalizeLang(i18n.language);

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (urlLangExplicit && i18n.language !== urlLangExplicit) {
      i18n.changeLanguage(urlLangExplicit);
    }
  }, [urlLangExplicit, i18n]);

  const loginHref = `/auth/login?next=${encodeURIComponent(nextPath)}&lang=${encodeURIComponent(langForLinks)}`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ email, password, displayName: displayName.trim() || null });
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err.message || t("auth.signupFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={handleSubmit}>
        <h2>{t("auth.registerTitle")}</h2>
        {error && <div className="auth-error">{error}</div>}
        <label>
          {t("auth.email")}
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          {t("auth.displayNameOptional")}
          <input
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>
        <label>
          {t("auth.password")}
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        <button type="submit" disabled={loading}>
          {loading ? t("auth.working") : t("auth.createAccount")}
        </button>
        <p className="auth-hint">
          {t("auth.alreadyHaveAccount")}{" "}
          <Link to={loginHref}>{t("auth.login")}</Link>
        </p>
        <LegalFooterLinks />
      </form>
    </div>
  );
}
